import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
} from "@clerk/clerk-react";
import { Menu, X, Zap } from "./Icons";

interface HeaderProps {
  onEnterApp?: () => void;
}

export function Header({ onEnterApp }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Helper to handle navigation: execute onEnterApp if provided, otherwise standard Link or just close menu
  const handleDashboardClick = () => {
    if (onEnterApp) {
      onEnterApp();
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="border-b border-slate-100 dark:border-slate-800 bg-white/50 dark:bg-slate-950/50 backdrop-blur-md fixed top-0 w-full z-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="bg-indigo-600 rounded-lg p-1.5">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-900 dark:text-white tracking-tight">
            taskplan
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          <nav className="flex items-center gap-8">
            <a
              href="/#use-cases"
              className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              Use Cases
            </a>
            <a
              href="/#features"
              className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              Features
            </a>
            <Link
              to="/pricing"
              className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              Pricing
            </Link>
          </nav>
          <div className="flex items-center gap-4">
            <SignedOut>
              <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                <button className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-4 py-2 transition-colors">
                  Sign In
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              {onEnterApp ? (
                <button
                  onClick={onEnterApp}
                  className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-4 py-2 transition-colors"
                >
                  Dashboard
                </button>
              ) : (
                <Link
                  to="/dashboard"
                  className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-4 py-2 transition-colors"
                >
                  Dashboard
                </Link>
              )}
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-4">
          <SignedIn>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 p-4 shadow-lg flex flex-col gap-4 animate-in slide-in-from-top-2">
          <nav className="flex flex-col gap-4">
            <a
              href="/#use-cases"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors p-2"
            >
              Use Cases
            </a>
            <a
              href="/#features"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors p-2"
            >
              Features
            </a>
            <Link
              to="/pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors p-2"
            >
              Pricing
            </Link>
          </nav>
          <div className="border-t border-slate-100 dark:border-slate-800 pt-4 flex flex-col gap-2">
            <SignedOut>
              <SignInButton mode="modal" forceRedirectUrl="/dashboard">
                <button className="w-full text-center text-base font-medium bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-3 rounded-lg transition-colors">
                  Sign In
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              {onEnterApp ? (
                <button
                  onClick={handleDashboardClick}
                  className="w-full text-center text-base font-medium bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-3 rounded-lg transition-colors"
                >
                  Dashboard
                </button>
              ) : (
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full text-center text-base font-medium bg-indigo-600 text-white hover:bg-indigo-700 px-4 py-3 rounded-lg transition-colors"
                >
                  Dashboard
                </Link>
              )}
            </SignedIn>
          </div>
        </div>
      )}
    </header>
  );
}
